import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import React, { useRef, useMemo, useEffect, useState } from "react";
import {
  Mesh,
  Group,
  Quaternion,
  Euler,
  MathUtils,
  MeshStandardMaterial,
} from "three";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { Keyframes } from "./Me3DAnimationFrames";
import { ArchHeightAnnotation } from "./ArchHeightAnnotations";
import { ARCH_HEIGHT_DATA } from "../../../constants/foot";

interface AnimationController {
  t: number;
  startIdx: number;
  endIdx: number;
}

interface GlbInsoleViewProps {
  insoleUri: string;
  controller: React.MutableRefObject<AnimationController>;
  keyframes: Keyframes;
  isLeft: boolean;
  controls?: {
    archIndex: React.MutableRefObject<number | undefined>;
    archNames: string[];
    archNamePlaceholder: string;
    patternRotation: React.MutableRefObject<number | undefined>;
    patternPlaceholder: string;
    zones: React.MutableRefObject<
      | {
        zoneCorrectionsAnterior: number;
        zoneCorrectionsHeel: number;
        zoneCorrectionsLateralAndAnterior: number;
        zoneCorrectionsMidfoot: number;
        zoneCorrectionsPosterior: number;
      }
      | undefined
    >;
    isArchScanned: React.MutableRefObject<boolean | undefined>;
  };
  stageRef: React.MutableRefObject<number>;
}

export const GlbInsoleView = ({
  insoleUri,
  controller,
  keyframes,
  stageRef,
  controls,
  isLeft,
}: GlbInsoleViewProps) => {
  const { invalidate } = useThree();
  const shaderMeshesRef = useRef<Mesh[]>([]);
  const insoleContainerRef = useRef<Group>(null);
  const [stage, setStage] = useState(stageRef.current);
  const [isKeyframeSettled, setIsKeyframeSettled] = useState(false);

  /** ARCH HEIGHT STATE **/
  // NOTE: declared up here (above the shader-patch effect) so that the
  // onBeforeCompile callback below can initialize the archHeight uniform
  // with the *current* animated value when a foam package toggle forces a
  // new clone/shader. If we initialized to 0 here, the first rendered frame
  // after the toggle would flatten the arch — and because frameloop is
  // "demand", the loop would go idle right after, leaving the insole
  // visually reset.
  const initialArchHeight = controls?.archIndex.current
    ? (controls.archIndex.current - 3) / 42
    : 0.07142857142;

  const currArchHeightRef = useRef(initialArchHeight);
  const currPatternAngleRef = useRef(controls?.patternRotation.current ?? 0);

  useFrame(() => {
    const newStage = Math.round(stageRef.current);
    if (newStage !== stage) {
      setStage(newStage);
    }
  });

  const gltf = useGLTF(insoleUri);

  // Clone the scene AND clone every material so this component owns its own
  // GPU-bound state. We never mutate the cached gltf.scene (drei's useGLTF
  // cache shares it across mounts), so cleanup on unmount is straightforward
  // and remounts don't pile up shader programs.
  const mesh = useMemo(() => {
    const cloned = gltf.scene.clone(true);
    cloned.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      if (Array.isArray(obj.material)) return;
      const clonedMat = obj.material.clone() as MeshStandardMaterial;
      // Disable vertex colors to prevent red tint from baked vertex data.
      // We re-enable it conditionally inside the shader patch below, but
      // only to read the green channel for arch deformation.
      clonedMat.vertexColors = false;
      clonedMat.needsUpdate = true;
      obj.material = clonedMat;
    });
    return cloned;
  }, [gltf.scene]);

  /**
   * Patch glTF Materials for arch manipulation
   * AND remove red tint from vertex colors.
   */
  useEffect(() => {
    const meshesWithShaders: Mesh[] = [];

    mesh.traverse((child) => {
      if (!(child as Mesh).isMesh) return;

      const meshChild = child as Mesh;

      if (!meshChild.geometry.attributes.color) {
        console.warn(
          "GLB Mesh missing vertex colors – arch may not work:",
          meshChild.name
        );
      }

      const material = meshChild.material;
      if (!material || Array.isArray(material)) return;
      material.vertexColors = true;

      material.onBeforeCompile = (shader) => {
        /** Remove vertex-color-based tinting */
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <color_fragment>",
          `
          // vertex colors disabled to prevent red tint
          // original: #include <color_fragment>
        `
        );

        /** Add arch deformation uniform — seed it with the currently animated
         *  arch height so a foam package toggle (which re-clones materials
         *  and recompiles shaders) doesn't flash the arch back to 0 on the
         *  first rendered frame. */
        shader.uniforms.archHeight = { value: currArchHeightRef.current };
        shader.vertexShader =
          "uniform float archHeight;\n" + shader.vertexShader;

        /** Apply deformation based on vertex green channel (color.g) */
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
          #include <begin_vertex>
          #ifdef USE_COLOR
            float g = clamp(color.g, 0.0, 1.0);
            transformed.y += g * archHeight / 106.0;
          #endif
        `
        );

        // Save shader reference so we can update uniform every frame
        // @ts-ignore
        meshChild.userData.shader = shader;
      };

      material.needsUpdate = true;
      meshesWithShaders.push(meshChild);
    });

    shaderMeshesRef.current = meshesWithShaders;

    invalidate();
  }, [mesh]);

  // Dispose this mount's cloned materials on unmount. Geometry and textures
  // are still shared with the cached gltf.scene (Object3D.clone() does NOT
  // deep-clone those), so we only dispose what we actually cloned —
  // otherwise we'd break drei's cached scene for the next mount.
  useEffect(() => {
    const localMesh = mesh;
    return () => {
      localMesh.traverse((obj) => {
        if (!(obj instanceof Mesh)) return;
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      });
      shaderMeshesRef.current = [];
    };
  }, [mesh]);

  /** Refs for indicators **/
  const patternIndicatorRef = useRef<Line2>(null);

  /** KEYFRAME STATE **/
  const maxKeyframeIdx = keyframes.positions.length - 1;
  const initialKeyframeIdx = Math.min(controller.current.startIdx || 0, maxKeyframeIdx);
  const targetPos = useRef(keyframes.positions[initialKeyframeIdx].clone());
  const targetRot = useRef(keyframes.rotations[initialKeyframeIdx].clone());
  const targetRotEased = useRef(keyframes.rotations[initialKeyframeIdx].clone());
  const tempRot = new Quaternion();

  /** FRAME LOOP **/
  useFrame((state, delta) => {
    if (!insoleContainerRef.current) return;

    // Clamp delta to avoid large jumps after idle periods in frameloop="demand"
    const clampedDelta = Math.min(delta, 0.064);

    /** Keyframe transitions - clamp indices to prevent out of bounds */
    const startIdx = Math.min(controller.current.startIdx, maxKeyframeIdx);
    const endIdx = Math.min(controller.current.endIdx, maxKeyframeIdx);

    // Safety check: ensure indices are within bounds
    const maxIdx = keyframes.positions.length - 1;
    const safeStartIdx = Math.min(Math.max(startIdx, 0), maxIdx);
    const safeEndIdx = Math.min(Math.max(endIdx, 0), maxIdx);

    targetPos.current.lerpVectors(
      keyframes.positions[safeStartIdx],
      keyframes.positions[safeEndIdx],
      controller.current.t
    );
    targetRot.current.slerpQuaternions(
      keyframes.rotations[safeStartIdx],
      keyframes.rotations[safeEndIdx],
      controller.current.t
    );

    insoleContainerRef.current.position.lerp(targetPos.current, clampedDelta * 6);
    targetRotEased.current.slerpQuaternions(
      tempRot.setFromEuler(insoleContainerRef.current.rotation),
      targetRot.current,
      clampedDelta * 4
    );
    insoleContainerRef.current.rotation.setFromQuaternion(
      targetRotEased.current
    );

    // Check if keyframe animation is settled (position and rotation are close to target)
    const positionDistance = insoleContainerRef.current.position.distanceTo(targetPos.current);
    const rotationDistance = insoleContainerRef.current.quaternion.angleTo(targetRot.current);
    const isPositionSettled = positionDistance < 0.01 && rotationDistance < 0.01;

    // Update settled state only when on stage 0 (front view)
    if (stage === 0) {
      if (isPositionSettled && !isKeyframeSettled) {
        setIsKeyframeSettled(true);
      } else if (!isPositionSettled && isKeyframeSettled) {
        setIsKeyframeSettled(false);
      }
    } else if (isKeyframeSettled) {
      // Reset when not on stage 0
      setIsKeyframeSettled(false);
    }

    /** Arch height animation */
    let archSettled = true;

    if (controls) {
      const targetArch =
        controls.archIndex.current !== undefined
          ? (controls.archIndex.current - 3) / 42
          : 0.07142857142;

      const { startIdx: ctrlStartIdx } = controller.current;

      // Every stage now lerps toward the REAL selected arch height — no more
      // placeholder wobble on the heel/lattice stages while the camera is
      // still moving. Stage 0 (front view, the first step) uses a slower
      // alpha so the bulge visibly grows in rather than snapping.
      const isFrontView = ctrlStartIdx === 0;

      currArchHeightRef.current = MathUtils.lerp(
        currArchHeightRef.current,
        targetArch,
        isFrontView ? clampedDelta : clampedDelta * 3
      );

      archSettled = Math.abs(currArchHeightRef.current - targetArch) < 0.001;

      // push value to shaders using cached mesh references
      for (const child of shaderMeshesRef.current) {
        if (child.userData.shader?.uniforms.archHeight) {
          child.userData.shader.uniforms.archHeight.value = currArchHeightRef.current;
        }
      }
    }

    // Keep requesting frames while any animation is still running
    if (!isPositionSettled || !archSettled) {
      invalidate();
    }
  });

  // Show arch height annotations only on first keyframe (front view) and when animation is settled
  const showArchAnnotations = controls?.isArchScanned.current && stage === 0 && isKeyframeSettled;

  // Get arch height label from controls
  const archHeight = controls && controls.archIndex.current !== undefined
    ? ARCH_HEIGHT_DATA[controls.archIndex.current].type
    : '';

  return (
    <group
      ref={insoleContainerRef}
      position={keyframes.positions[initialKeyframeIdx]}
      rotation={new Euler().setFromQuaternion(
        keyframes.rotations[initialKeyframeIdx]
      )}
    >
      <group position={[isLeft ? -0.05 : 0.05, 0, -0.4]}>
        <primitive object={mesh} rotation={[0, 0, 0]} scale={106} />
      </group>

      {/* Arch height annotation - only shown on first keyframe when animation is settled */}
      {showArchAnnotations && archHeight && (
        <ArchHeightAnnotation
          archHeight={archHeight}
          visible={true}
          isLeft={isLeft}
          archHeightValue={currArchHeightRef.current}
          startDelay={0.3} // Small delay after keyframe settles for smoother transition
        />
      )}
    </group>
  );
};
